import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const StructuredData = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Remove existing structured data
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());

    // Base business schema (always present)
    const businessSchema = {
      "@context": "https://schema.org",
      "@type": "DaySpaSpa",
      "name": "Ama Wellness & Spa",
      "alternateName": "Ama Spa",
      "description": i18n.language === 'es' 
        ? "Santuario de bienestar sagrado que ofrece tratamientos de lujo, tecnología biohacking y experiencias transformadoras en España."
        : "Sacred wellness sanctuary offering luxury treatments, biohacking technology, and transformative experiences in Spain.",
      "url": "https://amawellness.es",
      "logo": "https://amawellness.es/images/logo.jpg",
      "image": [
        "https://amawellness.es/images/logo.jpg",
        "https://amawellness.es/images/pool.png",
        "https://amawellness.es/images/rooms.png"
      ],
      "telephone": "+34-XXX-XXX-XXX", // Replace with actual phone
      "email": "info@amawellness.es", // Replace with actual email
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Your Street Address", // Replace with actual address
        "addressLocality": "Your City",
        "addressRegion": "Your Region", 
        "postalCode": "Your Postal Code",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 40.4168, // Replace with actual coordinates
        "longitude": -3.7038
      },
      "openingHours": [
        "Mo-Su 09:00-21:00"
      ],
      "priceRange": "€€€",
      "servedCuisine": [],
      "hasMenu": "https://amawellness.es/treatments",
      "acceptsReservations": true,
      "currenciesAccepted": "EUR",
      "paymentAccepted": ["Cash", "Credit Card"],
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Infrared Sauna",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification", 
          "name": "Chromotherapy",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Biohacking Technology",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Massage Therapy",
          "value": true
        }
      ],
      "sameAs": [
        "https://www.facebook.com/amawellnessspa", // Replace with actual social links
        "https://www.instagram.com/amawellnessspa",
        "https://www.linkedin.com/company/amawellnessspa"
      ]
    };

    // Page-specific schemas
    let pageSchema = null;

    // Get current path
    const currentPath = location.pathname;
    const isLanguagePath = currentPath.startsWith('/en/') || currentPath.startsWith('/es/');
    const basePath = isLanguagePath ? currentPath.substring(3) : currentPath;

    switch (basePath) {
      case '/':
      case '':
        // Homepage - Website schema
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Ama Wellness & Spa",
          "url": "https://amawellness.es",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://amawellness.es/treatments?search={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        };
        break;

      case '/treatments':
        // Treatments page - Service schema
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": i18n.language === 'es' ? "Tratamientos de Spa" : "Spa Treatments",
          "description": i18n.language === 'es' 
            ? "Menú completo de tratamientos sagrados incluyendo masajes, faciales, biohacking y rituales de bienestar."
            : "Complete menu of sacred treatments including massages, facials, biohacking, and wellness rituals.",
          "itemListElement": [
            {
              "@type": "Service",
              "name": i18n.language === 'es' ? "Masaje CBD" : "CBD Massage",
              "description": i18n.language === 'es' 
                ? "Masaje relajante con aceites CBD para alivio profundo del estrés y tensión muscular."
                : "Relaxing massage with CBD oils for deep stress relief and muscle tension release.",
              "provider": {
                "@type": "Organization",
                "name": "Ama Wellness & Spa"
              },
              "areaServed": "Spain",
              "availableChannel": {
                "@type": "ServiceChannel",
                "serviceUrl": "https://amawellness.es/treatments/cbd-massage"
              }
            },
            {
              "@type": "Service", 
              "name": i18n.language === 'es' ? "Cromoterapia Facial" : "Chromotherapy Facial",
              "description": i18n.language === 'es'
                ? "Facial avanzado con terapia de luz LED para rejuvenecimiento y sanación de la piel."
                : "Advanced facial with LED light therapy for skin rejuvenation and healing.",
              "provider": {
                "@type": "Organization",
                "name": "Ama Wellness & Spa"
              },
              "areaServed": "Spain"
            }
          ]
        };
        break;

      case '/contact':
        // Contact page - ContactPage schema
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": i18n.language === 'es' ? "Contacto - Ama Wellness & Spa" : "Contact - Ama Wellness & Spa",
          "description": i18n.language === 'es'
            ? "Contacta con Ama Wellness & Spa para reservar tratamientos y experiencias de bienestar."
            : "Contact Ama Wellness & Spa to book treatments and wellness experiences.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Ama Wellness & Spa"
          }
        };
        break;

      case '/journal':
        // Blog page - Blog schema
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": i18n.language === 'es' ? "Blog de Bienestar Sagrado" : "Sacred Wellness Blog",
          "description": i18n.language === 'es'
            ? "Explora sabiduría de bienestar, consejos de biohacking y prácticas de spa transformadoras."
            : "Explore wellness wisdom, biohacking tips, and transformative spa practices.",
          "url": "https://amawellness.es/journal",
          "publisher": {
            "@type": "Organization",
            "name": "Ama Wellness & Spa",
            "logo": {
              "@type": "ImageObject",
              "url": "https://amawellness.es/images/logo.jpg"
            }
          }
        };
        break;

      case '/wellness-quiz':
        // Quiz page - WebApplication schema
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": i18n.language === 'es' ? "Quiz de Bienestar Sagrado" : "Sacred Wellness Quiz",
          "description": i18n.language === 'es'
            ? "Quiz personalizado para descubrir los tratamientos perfectos para tus necesidades únicas."
            : "Personalized quiz to discover the perfect treatments for your unique needs.",
          "url": currentPath.startsWith('/') ? `https://amawellness.es${currentPath}` : `https://amawellness.es/wellness-quiz`,
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web Browser"
        };
        break;

      default:
        // Default WebPage schema for other pages
        pageSchema = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": document.title,
          "description": document.querySelector('meta[name="description"]')?.content || "",
          "url": window.location.href,
          "isPartOf": {
            "@type": "WebSite",
            "name": "Ama Wellness & Spa",
            "url": "https://amawellness.es"
          }
        };
    }

    // Create and append schemas
    const createSchemaScript = (schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 2);
      return script;
    };

    // Add business schema (always present)
    document.head.appendChild(createSchemaScript(businessSchema));
    
    // Add page-specific schema if it exists
    if (pageSchema) {
      document.head.appendChild(createSchemaScript(pageSchema));
    }

    console.log('Schema.org structured data added:', {
      business: 'DaySpaSpa',
      page: pageSchema?.['@type'] || 'None',
      language: i18n.language,
      path: currentPath
    });

    // Cleanup function
    return () => {
      const schemasToRemove = document.querySelectorAll('script[type="application/ld+json"]');
      schemasToRemove.forEach(script => script.remove());
    };
  }, [location.pathname, i18n.language]);

  return null; // This component doesn't render anything visible
};

export default StructuredData;
